import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

/*
 * Controlled pagination - the prototype's FIRST · PREV · 1..N · NEXT · LAST
 * bevel pills (active = orange). Renders a windowed page range so it stays
 * compact for large season/game lists.
 */

interface PaginationProps {
  page: number;
  lastPage: number;
  onPageChange: (page: number) => void;
  /** Max numbered pages shown around the current one. */
  window?: number;
  className?: string;
}

function pageWindow(page: number, lastPage: number, size: number): number[] {
  const half = Math.floor(size / 2);
  let start = Math.max(1, page - half);
  const end = Math.min(lastPage, start + size - 1);
  start = Math.max(1, end - size + 1);
  const pages: number[] = [];
  for (let p = start; p <= end; p += 1) pages.push(p);
  return pages;
}

const PILL =
  'bevel bg-btn text-titletext font-display text-[10px] uppercase px-2.5 py-1 outline-none transition-[filter] hover:brightness-112 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:opacity-40 disabled:pointer-events-none';

export function Pagination({
  page,
  lastPage,
  onPageChange,
  window = 4,
  className,
}: PaginationProps) {
  const { t } = useTranslation('common');
  const pages = useMemo(() => pageWindow(page, lastPage, window), [page, lastPage, window]);

  if (lastPage <= 1) return null;

  return (
    <nav
      className={cn('flex flex-wrap items-center justify-center gap-1.5', className)}
      aria-label={t('pagination.label')}
    >
      <button type="button" className={PILL} onClick={() => onPageChange(1)} disabled={page <= 1}>
        {t('pagination.first')}
      </button>
      <button
        type="button"
        className={PILL}
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
      >
        {t('pagination.prev')}
      </button>
      {pages.map((p) => (
        <button
          key={p}
          type="button"
          aria-current={p === page ? 'page' : undefined}
          className={cn(PILL, p === page && 'bevel-orange bg-orange-cta text-orange-cta-tx')}
          onClick={() => onPageChange(p)}
        >
          {p}
        </button>
      ))}
      <button
        type="button"
        className={PILL}
        onClick={() => onPageChange(page + 1)}
        disabled={page >= lastPage}
      >
        {t('pagination.next')}
      </button>
      <button
        type="button"
        className={PILL}
        onClick={() => onPageChange(lastPage)}
        disabled={page >= lastPage}
      >
        {t('pagination.last')}
      </button>
    </nav>
  );
}
